import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Music, AlertCircle, Shield, ArrowLeft, Mail, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(null);
  const [error, setError] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(true);
  
  // Password reset states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: request code, 2: enter code & new password
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Check if admin account exists on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await axios.get(`${API}/auth/status`);
        setIsFirstTime(!response.data.is_setup);
      } catch (err) {
        console.error('Error checking auth status:', err);
        setIsFirstTime(true);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkAuthStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isFirstTime) {
        if (password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }

        const response = await axios.post(`${API}/auth/setup`, { password });
        localStorage.setItem('admin_token', response.data.token);
        toast.success('Admin account created successfully!');
        onLogin(response.data.token);
      } else {
        const response = await axios.post(`${API}/auth/login`, { password });
        localStorage.setItem('admin_token', response.data.token);
        toast.success('Login successful!');
        onLogin(response.data.token);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setPassword('');
    }
  };

  const handleRequestResetCode = async () => {
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/auth/forgot-password`);
      toast.success('Reset code sent to your email!');
      setResetStep(2);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to send reset code. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        code: resetCode,
        new_password: newPassword
      });
      toast.success('Password reset successfully! You can now login.');
      // Reset all states and go back to login
      setShowForgotPassword(false);
      setResetStep(1);
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to reset password. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setResetStep(1);
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-amber-200 shadow-xl">
          <CardContent className="py-12 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Checking authentication status...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot Password Flow
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-amber-200 shadow-xl">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                <KeyRound className="w-8 h-8 text-amber-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Reset Password</CardTitle>
            <CardDescription>
              {resetStep === 1 
                ? "We'll send a reset code to your email" 
                : "Enter the code and your new password"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            {resetStep === 1 ? (
              // Step 1: Request reset code
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Mail className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      A 6-digit reset code will be sent to your notification email address.
                    </p>
                  </div>
                </div>
                
                <Button
                  onClick={handleRequestResetCode}
                  disabled={loading}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="send-reset-code-button"
                >
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </Button>
              </div>
            ) : (
              // Step 2: Enter code and new password
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="resetCode">Reset Code</Label>
                  <Input
                    id="resetCode"
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    required
                    maxLength={6}
                    className="border-amber-200 focus:border-amber-500 text-center text-2xl tracking-widest"
                    data-testid="reset-code-input"
                  />
                  <p className="text-xs text-gray-500 text-center">Code expires in 15 minutes</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Enter new password (min 6 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="border-amber-200 focus:border-amber-500"
                    data-testid="new-password-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="border-amber-200 focus:border-amber-500"
                    data-testid="confirm-password-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="reset-password-button"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRequestResetCode}
                  disabled={loading}
                  className="w-full"
                >
                  Resend Code
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Button
                variant="ghost"
                onClick={handleBackToLogin}
                className="text-amber-600 hover:text-amber-700"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Normal Login Flow
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-amber-200 shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
              <Music className="w-8 h-8 text-amber-600" />
            </div>
          </div>
          <CardTitle className="text-2xl">Guitar 101 Tuition</CardTitle>
          <CardDescription>
            {isFirstTime ? 'Set up your admin password' : 'Admin Login'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Security badge */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-700">Secure server-side authentication</span>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="password">
                {isFirstTime ? 'Create Password' : 'Password'}
              </Label>
              <Input
                id="password"
                type="password"
                placeholder={isFirstTime ? 'Enter a password (min 6 characters)' : 'Enter your password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="border-amber-200 focus:border-amber-500"
                data-testid="admin-password-input"
              />
              {isFirstTime && (
                <p className="text-xs text-gray-600">
                  This will be your admin password. Please remember it!
                </p>
              )}
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="admin-login-button"
            >
              {loading ? 'Please wait...' : isFirstTime ? 'Create Account' : 'Login'}
            </Button>
          </form>

          {/* Forgot Password Link - only show if not first time setup */}
          {!isFirstTime && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-amber-600 hover:text-amber-700 hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              onClick={() => window.location.href = '/'}
              className="text-amber-600 hover:text-amber-700"
            >
              Back to Website
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
